import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  attachRepo,
  detachRepo,
  fetchProjectRepos,
  fetchRepoOverrides,
  fetchEffectiveConfig,
  updateRepoOverrides,
  RepoInUseError,
} from '../../lib/api/projects';
import {
  REPO_FULL_NAME_PATTERN,
  type RepoMembershipSummary,
  type ProjectSettings,
  type EffectiveConfigEntry,
} from '../../../../shared/types/projects';
import { validateAll } from '../../../../shared/lib/projectValidation';

/**
 * FR-4 — attached repos list + attach + detach + override editor.
 * FR-5 — effective-config inspector is the right column when a repo is open.
 *
 * The override editor is intentionally simple here: a JSON textarea (not the
 * full Monaco view used at the project level) because per-repo overrides are
 * usually small. We surface "inheriting / overridden" indicators via the
 * effective-config side panel — clicking a row in that panel describes which
 * layer won, which is the same affordance as the spec's hint text.
 */
export const ReposTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [selected, setSelected] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const listQ = useQuery({
    queryKey: ['project', projectId, 'repos'],
    queryFn: () => fetchProjectRepos(projectId),
    enabled: projectId.length > 0,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <div className="w-1/2 space-y-3">
          <AttachForm
            projectId={projectId}
            onAttached={(r) => {
              queryClient.invalidateQueries({ queryKey: ['project', projectId, 'repos'] });
              setSelected(r.fullName);
            }}
          />
          <RepoList
            isLoading={listQ.isLoading}
            isError={listQ.isError}
            error={listQ.error as Error | null}
            repos={listQ.data ?? []}
            selected={selected}
            onSelect={setSelected}
            projectId={projectId}
            onDetach={() =>
              queryClient.invalidateQueries({ queryKey: ['project', projectId, 'repos'] })
            }
          />
        </div>
        <div className="w-1/2 space-y-3">
          {selected ? (
            <OverridePanel projectId={projectId} fullName={selected} />
          ) : (
            <div className="border rounded p-6 text-center text-sm text-gray-500 bg-gray-50">
              Select a repo on the left to edit its overrides or inspect the
              effective configuration.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Attach form
// ---------------------------------------------------------------------------

const AttachForm: React.FC<{
  projectId: string;
  onAttached: (r: RepoMembershipSummary) => void;
}> = ({ projectId, onAttached }) => {
  const [fullName, setFullName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: (req: { fullName: string }) => attachRepo(projectId, req),
    onSuccess: (r) => {
      onAttached(r);
      setFullName('');
      setSubmitted(false);
    },
  });

  const trimmed = fullName.trim();
  const malformed = submitted && trimmed !== '' && !REPO_FULL_NAME_PATTERN.test(trimmed);
  const empty = submitted && trimmed === '';

  return (
    <form
      className="border rounded p-3 space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitted(true);
        if (!trimmed || !REPO_FULL_NAME_PATTERN.test(trimmed)) return;
        mutation.mutate({ fullName: trimmed });
      }}
    >
      <header className="text-xs font-medium text-gray-700">Attach a repo</header>
      <div className="flex gap-2">
        <input
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="org/repo"
          aria-invalid={(empty || malformed) || undefined}
          className="flex-1 border rounded px-2 py-1 text-sm font-mono"
          disabled={mutation.isPending}
        />
        <button
          type="submit"
          disabled={mutation.isPending}
          data-testid="attach-submit"
          className="bg-blue-600 text-white text-sm px-3 rounded disabled:opacity-40 hover:bg-blue-700"
        >
          {mutation.isPending ? 'Attaching…' : 'Attach'}
        </button>
      </div>
      {empty && <p className="text-xs text-red-600">org/repo is required</p>}
      {malformed && <p className="text-xs text-red-600">Must look like "org/repo"</p>}
      {mutation.isError && (
        <p role="alert" className="text-xs text-red-600">
          {(mutation.error as Error).message}
        </p>
      )}
    </form>
  );
};

// ---------------------------------------------------------------------------
// Repo list
// ---------------------------------------------------------------------------

const RepoList: React.FC<{
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  repos: RepoMembershipSummary[];
  selected: string | null;
  onSelect: (fullName: string) => void;
  projectId: string;
  onDetach: () => void;
}> = ({ isLoading, isError, error, repos, selected, onSelect, projectId, onDetach }) => {
  if (isLoading) return <div className="text-sm text-gray-500">Loading repos…</div>;
  if (isError) {
    return <div role="alert" className="text-sm text-red-600">{error?.message}</div>;
  }
  if (repos.length === 0) {
    return (
      <div className="border rounded p-4 text-center text-sm text-gray-500 bg-gray-50">
        No repos attached yet.
      </div>
    );
  }
  return (
    <div className="border rounded overflow-hidden" data-testid="repos-list">
      {repos.map((r) => (
        <RepoRow
          key={r.fullName}
          repo={r}
          selected={selected === r.fullName}
          projectId={projectId}
          onSelect={() => onSelect(r.fullName)}
          onDetach={onDetach}
        />
      ))}
    </div>
  );
};

const RepoRow: React.FC<{
  repo: RepoMembershipSummary;
  selected: boolean;
  projectId: string;
  onSelect: () => void;
  onDetach: () => void;
}> = ({ repo, selected, projectId, onSelect, onDetach }) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleDetach = async (force: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      await detachRepo(projectId, repo.fullName, { force });
      onDetach();
    } catch (e) {
      if (e instanceof RepoInUseError) {
        setErr(
          `In-flight epic${e.blockingEpics.length === 1 ? '' : 's'} #${e.blockingEpics.join(', #')}.`,
        );
        setConfirming(true);
      } else {
        setErr((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={
        'flex items-center gap-2 px-3 py-2 border-b last:border-b-0 text-sm ' +
        (selected ? 'bg-blue-50' : 'hover:bg-gray-50')
      }
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 text-left font-mono text-xs"
      >
        {repo.fullName}
      </button>
      <div className="flex gap-1 text-[10px]">
        {repo.hasOverrides && (
          <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">overrides</span>
        )}
        {repo.hasGuardrails && (
          <span className="bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded">guardrails</span>
        )}
      </div>
      {!confirming ? (
        <button
          type="button"
          onClick={() => handleDetach(false)}
          disabled={busy}
          aria-label={`Detach ${repo.fullName}`}
          className="text-xs text-red-600 hover:underline disabled:opacity-40"
        >
          Detach
        </button>
      ) : (
        <div className="flex items-center gap-1 text-xs">
          {err && <span className="text-red-600">{err}</span>}
          <button
            type="button"
            onClick={() => handleDetach(true)}
            disabled={busy}
            className="text-red-700 underline"
          >
            Force
          </button>
          <button
            type="button"
            onClick={() => { setConfirming(false); setErr(null); }}
            className="text-gray-600 hover:underline"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Override panel + effective-config inspector
// ---------------------------------------------------------------------------

const OverridePanel: React.FC<{ projectId: string; fullName: string }> = ({
  projectId,
  fullName,
}) => {
  const overridesQ = useQuery({
    queryKey: ['project', projectId, 'repos', fullName, 'overrides'],
    queryFn: () => fetchRepoOverrides(projectId, fullName),
  });
  const effectiveQ = useQuery({
    queryKey: ['project', projectId, 'repos', fullName, 'effective'],
    queryFn: () => fetchEffectiveConfig(projectId, fullName),
  });

  if (overridesQ.isLoading) return <div className="text-sm text-gray-500">Loading…</div>;
  if (overridesQ.isError) {
    const m = (overridesQ.error as Error).message;
    return (
      <div role="alert" className="text-sm text-red-600">
        {m === 'NOT_FOUND' ? `Repo "${fullName}" not attached.` : m}
      </div>
    );
  }
  if (!overridesQ.data) return null;

  return (
    <div className="space-y-3">
      <OverrideEditor
        projectId={projectId}
        fullName={fullName}
        initial={overridesQ.data.overrides}
        onSaved={() => {
          overridesQ.refetch();
          effectiveQ.refetch();
        }}
      />
      <EffectivePanel
        isLoading={effectiveQ.isLoading}
        isError={effectiveQ.isError}
        error={effectiveQ.error as Error | null}
        entries={effectiveQ.data?.entries ?? []}
      />
    </div>
  );
};

const OverrideEditor: React.FC<{
  projectId: string;
  fullName: string;
  initial: ProjectSettings;
  onSaved: () => void;
}> = ({ projectId, fullName, initial, onSaved }) => {
  const [text, setText] = useState(() => JSON.stringify(initial, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);
  const [draft, setDraft] = useState<ProjectSettings>(initial);
  const [saveError, setSaveError] = useState<string | null>(null);

  const validation = validateAll(draft);

  const mutation = useMutation({
    mutationFn: (overrides: ProjectSettings) =>
      updateRepoOverrides(projectId, fullName, { overrides }),
    onSuccess: () => {
      setSaveError(null);
      onSaved();
    },
    onError: (e) => setSaveError((e as Error).message),
  });

  const handleChange = (next: string) => {
    setText(next);
    try {
      const parsed = JSON.parse(next) as ProjectSettings;
      setDraft(parsed);
      setParseError(null);
    } catch (e) {
      setParseError((e as Error).message);
    }
  };

  const handleSave = () => {
    setSaveError(null);
    if (parseError) return;
    if (!validation.ok) return;
    mutation.mutate(draft);
  };

  return (
    <div className="border rounded p-3 space-y-2">
      <header className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">Per-repo overrides (JSON)</span>
        <button
          type="button"
          onClick={handleSave}
          disabled={mutation.isPending || !!parseError || !validation.ok}
          data-testid="save-overrides"
          className="bg-blue-600 text-white text-xs px-3 py-1 rounded disabled:opacity-40 hover:bg-blue-700"
        >
          {mutation.isPending ? 'Saving…' : 'Save overrides'}
        </button>
      </header>
      <textarea
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        spellCheck={false}
        className="block w-full border rounded p-2 font-mono text-xs"
        rows={12}
      />
      {parseError && (
        <p role="alert" className="text-xs text-red-600">JSON: {parseError}</p>
      )}
      {!parseError && !validation.ok && (
        <div role="alert" className="text-xs text-red-600 space-y-0.5">
          {validation.errors.map((e, i) => <div key={i}>· {e}</div>)}
        </div>
      )}
      {saveError && (
        <p role="alert" className="text-xs text-red-600">{saveError}</p>
      )}
      <p className="text-[10px] text-gray-500">
        Only set the keys you want to override here — anything not set
        inherits from the project SETTINGS.
      </p>
    </div>
  );
};

const EffectivePanel: React.FC<{
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  entries: EffectiveConfigEntry[];
}> = ({ isLoading, isError, error, entries }) => {
  if (isLoading) return <div className="text-xs text-gray-500">Loading effective config…</div>;
  if (isError) return <div role="alert" className="text-xs text-red-600">{error?.message}</div>;
  return (
    <div className="border rounded p-3 space-y-1" data-testid="effective-config">
      <header className="text-xs font-medium text-gray-700">Effective configuration</header>
      <table className="w-full text-[11px] font-mono">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-0.5">key</th>
            <th className="py-0.5">value</th>
            <th className="py-0.5">source</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.keyPath} className="border-t">
              <td className="py-0.5 pr-2">{e.keyPath}</td>
              <td className="py-0.5 pr-2 truncate max-w-[200px]">
                {JSON.stringify(e.value)}
              </td>
              <td className="py-0.5">
                <SourceBadge source={e.source} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SourceBadge: React.FC<{ source: string }> = ({ source }) => {
  const cls =
    source === 'override'
      ? 'bg-blue-100 text-blue-800'
      : source === 'project'
      ? 'bg-green-100 text-green-800'
      : source === 'yaml'
      ? 'bg-orange-100 text-orange-800'
      : 'bg-gray-100 text-gray-700';
  return (
    <span className={`px-1 py-0.5 rounded text-[10px] ${cls}`}>{source}</span>
  );
};

export default ReposTab;
